/* Warehouse dropdown for FORM FIELDS — "which warehouse does this record belong
 * to?" on a PO / GRN / supplier / doctor / medicine-request.
 *
 * There used to be a second control here, WarehousePicker, that each screen
 * rendered to choose the warehouse it was showing. That is now one app-wide
 * selection in the topbar (Layout/WarehouseSwitcher + WarehouseContext), so the
 * scope control is gone and only the record field remains.
 *
 * A dropdown rather than a segmented row of buttons: there are 10+ warehouses
 * whose names run to "CUREBAY PHARMACY PRIVATE LIMITED (MANJHAUL…)", so
 * side-by-side buttons clipped every label to the same unreadable prefix. The code
 * leads each option because that is the part that differs between them.
 */
import React from "react";
import { SearchableSelect } from "./supplyKit";

/* Searchable warehouse dropdown for the many list filters and form fields that
   are not the page's role-aware scope control: "All warehouses" filters, and the
   "— Select warehouse —" field on a PO / GRN / supplier / doctor form.
   A head chooses between 10+ similarly-named warehouses, which is more than a
   plain <select> can be scanned in; the search box makes the list usable and the
   code leads each label because that is what distinguishes them.

   `disabled` (a PH user locked to their own warehouse) renders a static label
   rather than a dead dropdown — there is nothing to choose. */
export const WarehouseSelect = ({
  value,
  onChange,
  warehouses,
  noneLabel = "All warehouses",
  noneValue = "",
  disabled = false,
  width = 240,
}) => {
  const options = [
    { value: noneValue, label: noneLabel },
    ...(warehouses || []).map((w) => ({
      value: w.id,
      label: w.name ? `${w.id} · ${w.name}` : String(w.id),
    })),
  ];

  if (disabled) {
    const current = options.find((o) => String(o.value) === String(value));
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: 28,
          maxWidth: width,
          padding: "0 10px",
          border: "1px solid var(--border-default)",
          borderRadius: 4,
          background: "var(--bg-sunken)",
          color: "var(--fg-muted)",
          fontSize: 12,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={current?.label || value || ""}
      >
        {current?.label || value || "—"}
      </span>
    );
  }

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      aria-label="Warehouse"
      style={{ width, maxWidth: "100%" }}
      placeholder={noneLabel}
      options={options}
    />
  );
};

export default WarehouseSelect;
